/*
 * TICK-140 — the typed destination registry for both consoles (Figma page `07 — Screen Index &
 * Coverage`, node `38:3`).
 *
 * Thirty-three destinations exist and all thirty-three already work. What did not exist is one
 * place that states, per destination, what the shell needs to know about it: who may open it, which
 * phase of the work it belongs to, which of the six page templates it is drawn with, what its
 * primary action is, and which non-happy-path states it has to be able to render. Those facts were
 * spread across `NAV_SALES`/`NAV_OPS`/`NAV_PHASES`/`SECTION_LABEL`/`PAGE_META`/`tabAllowedFor` in
 * `Admin.tsx` and a parallel five in `Superadmin.tsx` — so a screen could be listed in one and
 * gated by another, and nothing checked that the two agreed.
 *
 * This file does not re-decide any of it. Every label, every role rule and every title below is the
 * value the console ships today; `console-destinations.test.ts` asserts that against the existing
 * `navPhasesFor()` and `superNavPhases()` rather than trusting this file, so a screen cannot be
 * added to a menu and forgotten here (or the reverse) without a red test.
 *
 * What is genuinely new is the last four fields — `template`, `primaryAction`, `states`, `route`.
 * They are the Figma contract, and they are data because the templates consume them: a screen gets
 * its filter toolbar and its filtered-empty panel because its entry says it is a `table`, not
 * because someone remembered to add one.
 */

/** The six page templates (Figma `08 — App Shell & Templates`, node `38:4`). */
export type PageTemplate =
  | 'dashboard'
  | 'table'
  | 'management'
  | 'editor'
  | 'operations'
  | 'safety';

/**
 * The feedback states a screen must be able to render (Figma `48:34` Feedback State Panel).
 *
 * Named `ScreenState` rather than `FeedbackState` because `FeedbackState` is the component that
 * draws one, and a type and a component sharing a name across two barrel exports is an ambiguity
 * the compiler is right to refuse.
 */
export type ScreenState = 'loading' | 'empty' | 'filteredEmpty' | 'error' | 'permission';

/** Access roles as the server issues them. `null` is a token that has not answered `whoami` yet. */
export type ConsoleRole = 'owner' | 'staff' | 'sales' | 'checkin';

export type ConsoleKind = 'eventAdmin' | 'superAdmin';

/** Event Admin source keys — the twenty values of `MainTab` in `Admin.tsx`. */
export type EventAdminKey =
  | 'command' | 'content' | 'tickets' | 'seatplan' | 'wristbands' | 'payments'
  | 'review' | 'delivery' | 'lineoa' | 'channels'
  | 'opsSettings' | 'opsTasks' | 'opsPeople' | 'opsRisks' | 'opsProcurement'
  | 'checkin' | 'opsShowday'
  | 'staff' | 'credits' | 'calendar';

/** SuperAdmin source keys — the thirteen values of `Tab` in `Superadmin.tsx`. */
export type SuperAdminKey =
  | 'overview' | 'events' | 'operations' | 'delivery' | 'tokens' | 'audit' | 'leads'
  | 'homeCms' | 'calendar' | 'lineOa' | 'credits' | 'vendorLogs' | 'flowMaps';

export type DestinationKey = EventAdminKey | SuperAdminKey;

/**
 * The journey phase a destination belongs to — the sidebar's grouping axis.
 *
 * The two consoles use different axes on purpose, and the reason is written in `Superadmin.tsx`:
 * an event has a life with an order, so Event Admin's phases are numbered; the platform console is
 * not attached to one event and its work has no sequence, so its areas are not.
 */
export type EventAdminPhase = 'today' | 'setup' | 'sell' | 'prepare' | 'showday' | 'settings';
export type SuperAdminPhase = 'today' | 'events' | 'commerce' | 'access' | 'platform' | 'reliability';
export type JourneyPhase = EventAdminPhase | SuperAdminPhase;

export interface Destination<K extends DestinationKey = DestinationKey> {
  /** The source key — the value `MainTab` / `Tab` already uses. Never renamed: it is the contract. */
  readonly key: K;
  readonly console: ConsoleKind;
  /** Where the destination lives. `:eventSlug` is substituted by the shell. */
  readonly route: string;
  /** Roles that may open it. Event Admin gates four screens to `owner`; SuperAdmin has one role. */
  readonly roles: readonly ConsoleRole[];
  /**
   * The server-side permission the screen depends on, named as the API expresses it. Presentation
   * never enforces this — the endpoint does — but the shell needs it to render a permission panel
   * instead of an empty table when a role reaches a screen its token cannot load.
   */
  readonly permission: string;
  readonly phase: JourneyPhase;
  /** Sidebar/section-tab label. Short form where the destination heading already says it. */
  readonly navLabel: string;
  readonly title: string;
  readonly description: string;
  readonly template: PageTemplate;
  /**
   * The one highest-emphasis action on the screen, or `null` where the screen is read-only.
   *
   * **The screen is the source of truth, not this field.** Nothing reads it — the shell's own
   * `primaryAction` is a separate node the page supplies — so it drifted from what the console
   * actually says, and TICK-142 found nine entries naming buttons that were worded differently
   * or, in one case, no longer exist. It describes the product; when the two disagree, this is
   * the side that is wrong.
   *
   * Written without the leading `＋` some buttons carry: that glyph is ornament on the control,
   * not part of the action's name, and including it would make this field track styling.
   */
  readonly primaryAction: string | null;
  readonly states: readonly ScreenState[];
}

/* Every screen can fail to load and can be opened by a token that may not load it. */
const BASE_STATES: readonly ScreenState[] = ['loading', 'error', 'permission'];
/* A list screen adds "there is nothing" and "your filter matched nothing" — different sentences. */
const LIST_STATES: readonly ScreenState[] = [...BASE_STATES, 'empty', 'filteredEmpty'];
/* A screen with a collection but no filters can be empty without being filtered-empty. */
const COLLECTION_STATES: readonly ScreenState[] = [...BASE_STATES, 'empty'];

/*
 * The three role sets, reproducing `tabAllowedFor` in `Admin.tsx` exactly rather than restating the
 * rule in a second shape. That function decides two things and nothing else:
 *
 *   - a `sales` token may open `command` and `review`, and no other screen;
 *   - `payments`, `credits`, `lineoa` and `opsProcurement` are `owner` only.
 *
 * Everything else follows, and that is why `checkin` appears in the two sets below: it is not a
 * product decision made here, it is what the shipped rule returns. `tabAllowedFor` never names
 * `checkin`, so a check-in token falls through both clauses and is allowed sixteen screens.
 *
 * That is very likely a defect — a check-in token is issued for `/staff/checkin`, and `Admin.tsx`
 * types its own role state as `'owner' | 'staff' | 'sales' | null`, so nobody intended this console
 * to have a fourth role. It is reproduced rather than fixed because narrowing an access rule inside
 * a visual redesign is how a redesign gets blamed for locking someone out; it is raised as its own
 * ticket instead, with the server-side check as the thing to confirm first.
 */
const OWNER_ONLY: readonly ConsoleRole[] = ['owner'];
const OPERATIONAL: readonly ConsoleRole[] = ['owner', 'staff', 'checkin'];
const OPERATIONAL_AND_SALES: readonly ConsoleRole[] = ['owner', 'staff', 'sales', 'checkin'];

const EA = '/e/:eventSlug/admin';

/**
 * The twenty Event Admin destinations, in sidebar order.
 *
 * Roles mirror `tabAllowedFor` exactly: a `sales` token sees `command` and `review` and nothing
 * else, and `payments`/`credits`/`lineoa`/`opsProcurement` are owner-only. A `checkin` token holds
 * no console screens at all — it is issued for `/staff/checkin`, which is not part of this shell.
 */
export const EVENT_ADMIN_DESTINATIONS: readonly Destination<EventAdminKey>[] = [
  {
    key: 'command', console: 'eventAdmin', route: `${EA}?tab=command`,
    roles: OPERATIONAL_AND_SALES, permission: 'event:read', phase: 'today',
    navLabel: 'Command Center', title: 'Command Center',
    description: 'สิ่งที่ต้องทำตอนนี้ แล้วต่อด้วยยอดขาย โซน ช่องทาง และแนวโน้มรายได้ — ตัวเลขทุกตัวมาจาก endpoint ที่มีอยู่จริง',
    template: 'dashboard', primaryAction: 'รีเฟรช', states: BASE_STATES,
  },
  {
    key: 'content', console: 'eventAdmin', route: `${EA}?tab=content`,
    roles: OPERATIONAL, permission: 'event:write', phase: 'setup',
    navLabel: 'หน้า Landing Page', title: 'หน้า Landing Page',
    description: 'แก้ไขข้อมูลที่แสดงบนหน้าขายบัตร',
    template: 'editor', primaryAction: 'บันทึกทั้งหมด', states: BASE_STATES,
  },
  {
    key: 'tickets', console: 'eventAdmin', route: `${EA}?tab=tickets`,
    roles: OPERATIONAL, permission: 'ticket:write', phase: 'setup',
    navLabel: 'บัตรและราคา', title: 'จัดการตั๋ว',
    description: 'ราคา โควตา และการเปิด/ปิดการขายแต่ละประเภท',
    template: 'management', primaryAction: 'สร้างประเภทตั๋ว', states: COLLECTION_STATES,
  },
  {
    key: 'seatplan', console: 'eventAdmin', route: `${EA}?tab=seatplan`,
    roles: OPERATIONAL, permission: 'seat:write', phase: 'setup',
    navLabel: 'ผังที่นั่ง', title: 'ผังที่นั่ง',
    description: 'แบ่งโซนตามสี สร้างผังที่นั่ง และดูว่าที่นั่งไหนถูกจองแล้ว — ราคาของแต่ละโซนคือราคาของประเภทบัตรนั้น',
    template: 'operations', primaryAction: 'สร้างโซน', states: COLLECTION_STATES,
  },
  {
    key: 'wristbands', console: 'eventAdmin', route: `${EA}?tab=wristbands`,
    roles: OPERATIONAL, permission: 'ticket:write', phase: 'setup',
    navLabel: 'Wristband', title: 'Wristband',
    description: 'สร้างริสแบนด์และผูกกับประเภทตั๋ว',
    template: 'management', primaryAction: 'สร้าง Wristband', states: COLLECTION_STATES,
  },
  {
    key: 'payments', console: 'eventAdmin', route: `${EA}?tab=payments`,
    roles: OWNER_ONLY, permission: 'payment:write', phase: 'setup',
    navLabel: 'บัญชีรับเงิน', title: 'บัญชีรับเงิน',
    description: 'บัญชีที่ใช้สร้าง QR รับเงินและตรวจสลิปของงานนี้',
    template: 'editor', primaryAction: 'เพิ่มบัญชี', states: COLLECTION_STATES,
  },
  {
    key: 'review', console: 'eventAdmin', route: `${EA}?tab=review`,
    roles: OPERATIONAL_AND_SALES, permission: 'order:read', phase: 'sell',
    navLabel: 'ตรวจสอบสลิป', title: 'ตรวจสอบสลิป',
    description: 'ตรวจสอบสลิปโอนเงินและจัดการออร์เดอร์ตามสถานะ',
    template: 'table', primaryAction: 'รีเฟรช', states: LIST_STATES,
  },
  {
    /*
     * The scope document lists this screen as owner-only; `tabAllowedFor` does not, and its
     * owner-only list is exactly four keys — payments, credits, lineoa, opsProcurement. The code is
     * what production enforces, so the code is what this says, and the document is the thing to
     * amend. Narrowing it here would remove a screen from every staff token in a redesign.
     */
    key: 'delivery', console: 'eventAdmin', route: `${EA}?tab=delivery`,
    roles: OPERATIONAL, permission: 'event:write', phase: 'sell',
    navLabel: 'การส่งมอบตั๋ว', title: 'การส่งมอบตั๋ว',
    description: 'ผู้ซื้อจะเห็นตั๋วเมื่อไหร่ และระบบส่งอะไรตามไปให้บ้างหลังชำระเงิน',
    template: 'safety', primaryAction: 'บันทึก', states: BASE_STATES,
  },
  {
    key: 'lineoa', console: 'eventAdmin', route: `${EA}?tab=lineoa`,
    roles: OWNER_ONLY, permission: 'integration:write', phase: 'sell',
    navLabel: 'LINE OA ของผู้จัด', title: 'LINE OA ของผู้จัด',
    description: 'ปุ่มเพิ่มเพื่อน OA ของผู้จัด และการเชื่อมต่อ LINE ของงานนี้',
    template: 'safety', primaryAction: 'บันทึก', states: BASE_STATES,
  },
  {
    key: 'channels', console: 'eventAdmin', route: `${EA}?tab=channels`,
    roles: OPERATIONAL, permission: 'order:read', phase: 'sell',
    navLabel: 'ช่องทางการขาย', title: 'ช่องทางการขาย',
    description: 'สร้างลิงก์ให้ผู้ลงคอนเทนต์แต่ละคน แล้วดูว่าใครพาคนมาซื้อ',
    template: 'table', primaryAction: 'สร้างลิงก์', states: LIST_STATES,
  },
  {
    key: 'opsSettings', console: 'eventAdmin', route: `${EA}?tab=opsSettings`,
    roles: OPERATIONAL, permission: 'ops:write', phase: 'prepare',
    navLabel: 'ข้อมูลการจัดงาน', title: 'ข้อมูลการจัดงาน',
    description: 'เป้าหมายและลักษณะงานที่ทีมงานใช้วางแผน — ไม่เกี่ยวกับราคาบัตรหรือสิ่งที่ผู้ซื้อเห็น',
    template: 'editor', primaryAction: 'บันทึกการตั้งค่า', states: BASE_STATES,
  },
  {
    key: 'opsTasks', console: 'eventAdmin', route: `${EA}?tab=opsTasks`,
    roles: OPERATIONAL, permission: 'ops:write', phase: 'prepare',
    navLabel: 'แผนงาน', title: 'แผนงาน',
    description: 'งานของอีเวนต์นี้ ใครรับผิดชอบ กำหนดส่ง และงานไหนต้องเสร็จก่อนงานไหน',
    template: 'management', primaryAction: 'เพิ่มงาน', states: LIST_STATES,
  },
  {
    key: 'opsPeople', console: 'eventAdmin', route: `${EA}?tab=opsPeople`,
    roles: OPERATIONAL, permission: 'ops:write', phase: 'prepare',
    navLabel: 'ทีมงานและฝ่าย', title: 'ทีมงานและฝ่าย',
    description: 'รายชื่อทีมงานของอีเวนต์นี้และฝ่ายที่สังกัด — เบอร์และอีเมลถูกปิดบังในรายชื่อ กดดูรายคนจึงเห็นค่าจริง',
    template: 'table', primaryAction: 'เพิ่มทีมงาน', states: LIST_STATES,
  },
  {
    key: 'opsRisks', console: 'eventAdmin', route: `${EA}?tab=opsRisks`,
    roles: OPERATIONAL, permission: 'ops:write', phase: 'prepare',
    navLabel: 'ความเสี่ยง', title: 'ความเสี่ยง',
    description: 'ทะเบียนความเสี่ยงของอีเวนต์นี้ · คะแนนคำนวณจากโอกาสเกิด × ผลกระทบ และการลงนามยอมรับบันทึกว่าใครเป็นคนรับ',
    template: 'table', primaryAction: 'เพิ่มความเสี่ยง', states: LIST_STATES,
  },
  {
    key: 'opsProcurement', console: 'eventAdmin', route: `${EA}?tab=opsProcurement`,
    roles: OWNER_ONLY, permission: 'ops:finance', phase: 'prepare',
    navLabel: 'จัดซื้อและการตลาด', title: 'จัดซื้อและการตลาด',
    description: 'ใบขอราคา ข้อเสนอของผู้ขาย และค่าโฆษณา — เจ้าของงานเท่านั้น',
    template: 'table', primaryAction: 'สร้าง RFQ', states: LIST_STATES,
  },
  {
    key: 'checkin', console: 'eventAdmin', route: `${EA}?tab=checkin`,
    roles: OPERATIONAL, permission: 'checkin:write', phase: 'showday',
    navLabel: 'เช็กอินหน้างาน', title: 'เช็กอินหน้างาน',
    description: 'สแกน QR หรือกรอกรหัสบัตรเพื่อยืนยันเข้างาน',
    template: 'operations', primaryAction: 'ค้นหา', states: BASE_STATES,
  },
  {
    key: 'opsShowday', console: 'eventAdmin', route: `${EA}?tab=opsShowday`,
    roles: OPERATIONAL, permission: 'ops:write', phase: 'showday',
    navLabel: 'คิวและลงของ', title: 'คิวและลงของ',
    description: 'ลำดับคิวบนเวทีและตารางรถเข้าลงของ — ตารางแสดงทะเบียนรถแต่ปิดบังเบอร์คนขับ',
    template: 'operations', primaryAction: 'เพิ่มคิว', states: LIST_STATES,
  },
  {
    key: 'staff', console: 'eventAdmin', route: `${EA}?tab=staff`,
    roles: OPERATIONAL, permission: 'token:write', phase: 'settings',
    navLabel: 'ผู้ใช้ & Token', title: 'ผู้ใช้ & Token',
    description: 'สร้าง Token ให้พนักงานเช็กอิน และดูสถิติการเช็กอินของแต่ละคน',
    template: 'management', primaryAction: 'ออก Token ใหม่', states: COLLECTION_STATES,
  },
  {
    key: 'credits', console: 'eventAdmin', route: `${EA}?tab=credits`,
    roles: OWNER_ONLY, permission: 'credit:read', phase: 'settings',
    navLabel: 'เครดิตผู้จัดงาน', title: 'เครดิตผู้จัดงาน',
    description: 'เครดิตที่ใช้ตัดค่าบริการต่อผู้เข้างาน 1 คน — ซื้อ ดูวันหมดอายุ และแลกเครดิตคงเหลือเป็นส่วนลด',
    template: 'management', primaryAction: 'ซื้อเครดิต', states: COLLECTION_STATES,
  },
  {
    key: 'calendar', console: 'eventAdmin', route: `${EA}?tab=calendar`,
    roles: OPERATIONAL, permission: 'calendar:read', phase: 'settings',
    navLabel: 'ปฏิทินคอนเสิร์ต', title: 'ปฏิทินคอนเสิร์ต',
    description: 'ดูตารางงานคอนเสิร์ตอื่นเพื่อเลี่ยงวันชนกัน (ข้อมูลภายใน ไม่เปิดสาธารณะ)',
    template: 'operations', primaryAction: null, states: COLLECTION_STATES,
  },
];

const SA = '/superadmin';

/**
 * The thirteen SuperAdmin destinations, in sidebar order.
 *
 * One role: a SuperAdmin token opens every screen here, which is why each entry still carries a
 * `permission` — the screens differ in what the *server* will answer, and the difference has to
 * survive somewhere a reviewer can read.
 */
export const SUPER_ADMIN_DESTINATIONS: readonly Destination<SuperAdminKey>[] = [
  {
    key: 'overview', console: 'superAdmin', route: `${SA}?tab=overview`,
    roles: OWNER_ONLY, permission: 'platform:read', phase: 'today',
    navLabel: 'ภาพรวมแพลตฟอร์ม', title: 'ภาพรวมแพลตฟอร์ม',
    description: 'รวมทุกอีเวนต์ที่รันอยู่บนแพลตฟอร์ม',
    template: 'dashboard', primaryAction: 'รีเฟรช', states: BASE_STATES,
  },
  {
    key: 'events', console: 'superAdmin', route: `${SA}?tab=events`,
    roles: OWNER_ONLY, permission: 'platform:write', phase: 'events',
    navLabel: 'อีเวนต์ / Workspaces', title: 'อีเวนต์ / Workspaces',
    description: 'แต่ละอีเวนต์คือ workspace แยกกัน มีหน้าขายและทีมงานของตัวเอง',
    template: 'management', primaryAction: 'สร้างอีเวนต์', states: LIST_STATES,
  },
  {
    key: 'operations', console: 'superAdmin', route: `${SA}?tab=operations`,
    roles: OWNER_ONLY, permission: 'platform:read', phase: 'commerce',
    navLabel: 'ธุรกรรม & การส่งบัตร', title: 'ธุรกรรม & การส่งบัตร',
    description: 'ติดตามผลตรวจสอบการชำระเงินและสถานะส่งบัตรในปลายทางเดียว',
    template: 'table', primaryAction: null, states: LIST_STATES,
  },
  {
    key: 'delivery', console: 'superAdmin', route: `${SA}?tab=delivery`,
    roles: OWNER_ONLY, permission: 'platform:switch', phase: 'commerce',
    navLabel: 'สวิตช์การส่งมอบตั๋ว', title: 'สวิตช์การส่งมอบตั๋ว',
    description: 'สวิตช์ระดับแพลตฟอร์มที่ปิดได้ทันทีโดยไม่ต้อง deploy — ช่องทาง LINE และหน้า /myticket',
    template: 'safety', primaryAction: null, states: BASE_STATES,
  },
  {
    key: 'tokens', console: 'superAdmin', route: `${SA}?tab=tokens`,
    roles: OWNER_ONLY, permission: 'token:write', phase: 'access',
    navLabel: 'ผู้ใช้ & Token', title: 'ผู้ใช้ & Token',
    description: 'ออก Token การเข้าถึง แยกตามอีเวนต์และบทบาท · เพิกถอนได้ทุกเมื่อ',
    template: 'management', primaryAction: 'ออก Token ใหม่', states: LIST_STATES,
  },
  {
    key: 'audit', console: 'superAdmin', route: `${SA}?tab=audit`,
    roles: OWNER_ONLY, permission: 'audit:read', phase: 'access',
    navLabel: 'บันทึกการใช้งาน', title: 'บันทึกการใช้งาน',
    description: 'กิจกรรมทั้งหมดข้ามอีเวนต์ — ใครทำอะไร ที่ไหน เมื่อไร',
    template: 'table', primaryAction: null, states: LIST_STATES,
  },
  {
    key: 'leads', console: 'superAdmin', route: `${SA}?tab=leads`,
    roles: OWNER_ONLY, permission: 'lead:read', phase: 'access',
    navLabel: 'Leads จากหน้าแรก', title: 'Leads from Home Page',
    description: 'รายชื่อผู้จัดงานที่ส่งข้อมูลติดต่อกลับมาจากหน้าแรก',
    template: 'table', primaryAction: null, states: LIST_STATES,
  },
  {
    key: 'homeCms', console: 'superAdmin', route: `${SA}?tab=homeCms`,
    roles: OWNER_ONLY, permission: 'cms:write', phase: 'platform',
    navLabel: 'หน้าหลัก (Home CMS)', title: 'จัดการหน้าหลัก (Home CMS)',
    description: 'ตั้งค่าการแสดงผล ลำดับ แบนเนอร์ และส่วนสำหรับผู้จัดงาน บนหน้า Portal',
    template: 'editor', primaryAction: 'บันทึก', states: BASE_STATES,
  },
  {
    key: 'calendar', console: 'superAdmin', route: `${SA}?tab=calendar`,
    roles: OWNER_ONLY, permission: 'calendar:write', phase: 'platform',
    navLabel: 'ปฏิทินงานคอนเสิร์ต', title: 'ปฏิทินงานคอนเสิร์ต',
    description: 'ตารางงานคอนเสิร์ตและแฟนมีตติ้งทั่วประเทศไทย — ข้อมูลภายในสำหรับทีมงานเลี่ยงวันชนกัน ไม่เปิดสาธารณะ',
    /*
     * Briefly 'เลือกไฟล์', while adding a show by hand did not exist in the rebuilt screen and
     * importing a file was the only way in. It exists again, so this names it again. Importing
     * from a URL is still gone — see TICK-142; the label does not cover for it.
     */
    template: 'operations', primaryAction: 'เพิ่มงาน', states: COLLECTION_STATES,
  },
  {
    key: 'lineOa', console: 'superAdmin', route: `${SA}?tab=lineOa`,
    roles: OWNER_ONLY, permission: 'integration:write', phase: 'platform',
    navLabel: 'LINE OA', title: 'LINE OA',
    description: 'LINE OA ของ JamTick และของผู้จัดแต่ละงาน — สถานะ การตรวจสอบ และการเปลี่ยน credential',
    template: 'safety', primaryAction: null, states: COLLECTION_STATES,
  },
  {
    key: 'credits', console: 'superAdmin', route: `${SA}?tab=credits`,
    roles: OWNER_ONLY, permission: 'credit:write', phase: 'platform',
    navLabel: 'เครดิตผู้จัดงาน', title: 'เครดิตผู้จัดงาน',
    description: 'ยอดเครดิต ยอดค้างจ่าย การให้เครดิต และการตรวจรายการเติมเงิน — ทุกการกระทำถูกบันทึกใน audit log',
    template: 'table', primaryAction: 'ให้เครดิต', states: LIST_STATES,
  },
  {
    key: 'vendorLogs', console: 'superAdmin', route: `${SA}?tab=vendorLogs`,
    roles: OWNER_ONLY, permission: 'audit:read', phase: 'reliability',
    navLabel: 'การตอบกลับจากผู้ให้บริการ', title: 'การตอบกลับจากผู้ให้บริการ',
    description: 'คำตอบที่ Thunder / EasySlip / ThaiBulkSMS / Resend ส่งกลับมาจริง — ใช้ตรวจสอบเมื่อระบบให้ผลไม่ตรงกับที่คาด',
    template: 'table', primaryAction: null, states: LIST_STATES,
  },
  {
    key: 'flowMaps', console: 'superAdmin', route: `${SA}?tab=flowMaps`,
    roles: OWNER_ONLY, permission: 'platform:read', phase: 'reliability',
    navLabel: 'แผนผังการทำงานของระบบ', title: 'แผนผังการทำงานของระบบ',
    description: 'ลำดับการทำงานจริงของแต่ละฟีเจอร์ อ่านจาก docs/flow-maps โดยตรง — ใช้ไล่ปัญหาและอธิบายระบบให้ทีมใหม่',
    template: 'operations', primaryAction: null, states: COLLECTION_STATES,
  },
];

/** All thirty-three, in one list. The order is sidebar order within each console. */
export const ALL_DESTINATIONS: readonly Destination[] = [
  ...EVENT_ADMIN_DESTINATIONS,
  ...SUPER_ADMIN_DESTINATIONS,
];

/**
 * The phase headings, per console.
 *
 * Event Admin's are numbered because an event's life has an order and the four numbered ones are
 * the four `PhaseKey`s `readiness.ts` counts — the menu and the readiness strip have to name the
 * same thing the same way. SuperAdmin's are areas of responsibility with no sequence, so they are
 * not numbered.
 */
export const EVENT_ADMIN_PHASE_LABEL: Record<EventAdminPhase, string> = {
  today: 'Command Center',
  setup: '02 · ตั้งค่าอีเวนต์',
  sell: '03 · ขายและส่งมอบ',
  prepare: '04 · เตรียมงาน',
  showday: '05 · วันแสดง',
  settings: '06 · ตั้งค่าและสิทธิ์',
};

export const SUPER_ADMIN_PHASE_LABEL: Record<SuperAdminPhase, string> = {
  today: 'ภาพรวมแพลตฟอร์ม',
  events: 'อีเวนต์ / Workspaces',
  commerce: 'ธุรกรรมและการส่งบัตร',
  access: 'การเข้าถึงและบันทึก',
  platform: 'Portal และการเชื่อมต่อ',
  reliability: 'ความน่าเชื่อถือของระบบ',
};

const PHASE_ORDER: Record<ConsoleKind, readonly JourneyPhase[]> = {
  eventAdmin: ['today', 'setup', 'sell', 'prepare', 'showday', 'settings'],
  superAdmin: ['today', 'events', 'commerce', 'access', 'platform', 'reliability'],
};

/** Lookup by console + source key. Two maps, because `delivery`, `credits` and `calendar` are
 *  legitimate keys in *both* consoles and mean different screens in each. */
const BY_KEY: Record<ConsoleKind, ReadonlyMap<string, Destination>> = {
  eventAdmin: new Map(EVENT_ADMIN_DESTINATIONS.map((d) => [d.key, d as Destination])),
  superAdmin: new Map(SUPER_ADMIN_DESTINATIONS.map((d) => [d.key, d as Destination])),
};

export function destinationFor(console: ConsoleKind, key: string): Destination | undefined {
  return BY_KEY[console].get(key);
}

/**
 * Whether a role may open a destination. The same decision `tabAllowedFor` makes, read from data.
 *
 * `null` — no answer from `whoami` yet — is not a role and gets `false`. This is the function the
 * shell asks before rendering a screen's contents, and answering "yes, probably" to an unknown
 * token is how a permission panel turns into a half-rendered screen. The *menu* answers this
 * differently, on purpose: see `navigationFor`.
 */
export function canAccess(destination: Destination, role: ConsoleRole | null): boolean {
  return role !== null && destination.roles.includes(role);
}

export interface NavPhase {
  readonly key: JourneyPhase;
  readonly label: string;
  readonly items: readonly Destination[];
}

/**
 * The sidebar for a role: phases in order, each holding the destinations that role may open, with
 * empty phases dropped.
 *
 * Dropping an empty phase is the rule that keeps a `sales` token at one destination instead of six
 * mostly-locked ones — a menu that opens onto nothing is worse than a menu that does not offer it.
 */
export function navigationFor(console: ConsoleKind, role: ConsoleRole | null): NavPhase[] {
  const all = console === 'eventAdmin' ? EVENT_ADMIN_DESTINATIONS : SUPER_ADMIN_DESTINATIONS;
  /*
   * An unknown role draws the menu a non-owner token would get, which is what the console does
   * today in the moment before `whoami` answers. Rendering an empty sidebar for that half-second
   * and then filling it is a worse answer than rendering the conservative one and revealing four
   * more items — and `canAccess` still says `false` for `null`, so nothing *inside* a screen is
   * shown on the strength of this.
   */
  const menuRole: ConsoleRole = role ?? 'staff';
  const label = console === 'eventAdmin'
    ? (p: JourneyPhase) => EVENT_ADMIN_PHASE_LABEL[p as EventAdminPhase]
    : (p: JourneyPhase) => SUPER_ADMIN_PHASE_LABEL[p as SuperAdminPhase];

  return PHASE_ORDER[console]
    .map((phase) => ({
      key: phase,
      label: label(phase),
      items: all.filter((d) => d.phase === phase && canAccess(d as Destination, menuRole)) as readonly Destination[],
    }))
    .filter((p) => p.items.length > 0);
}

/** The phase a key belongs to — what the sidebar marks active, derived from the open screen rather
 *  than held as its own state, so every existing `setTab` call lights the right destination. */
export function activePhaseFor(console: ConsoleKind, key: string): JourneyPhase | null {
  return destinationFor(console, key)?.phase ?? null;
}

/** The route for a destination with `:eventSlug` filled in. */
export function routeFor(destination: Destination, eventSlug?: string): string {
  return eventSlug ? destination.route.replace(':eventSlug', eventSlug) : destination.route;
}
